import React, { useEffect, useRef, useState, useLayoutEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowBack } from "@mui/icons-material";
import styles from "./CognitiveCheck.module.css";
import { generateTestSetId, setCognitiveTestSetId } from "../utils/testId";
import { getUserId } from "../utils/userId";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../utils/firebase";
import cognitiveData from "../data/cognitive.json";

const getScoreCircleClass = (score: number, threshold: number, totalCount: number): string => {
  if (score <= threshold) return styles.scoreCircleLow;
  
  if (totalCount > 0) {
    const percentage = (score / totalCount) * 100;
    if (percentage >= 71) return styles.scoreCircleHigh;
  }
  return styles.scoreCircleMedium;
};

const CognitiveCheck: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { hospitalId } = useParams();
  const prefix = hospitalId ? `/${hospitalId}` : '';

  const [responseHistory, setResponseHistory] = useState<any[]>([]); 
  const [isLoading, setIsLoading] = useState(true); 

  const contentRef = useRef<HTMLDivElement>(null);
  const scrollTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const testMapper = {
    test1: "digit_span",
    test2: "random_cube",
    test3: "verbal_learning_test",
    test4: "tower_of_london",
  };

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0; 

    if (contentRef.current) contentRef.current.scrollTop = 0;

    const timer = setTimeout(() => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  // --- 2. 핸들러 함수들 ---
  const handleBack = () => {
    navigate(`${prefix}/check`);
  };

  // 테스트 시작 핸들러
  const handleStartTest = () => {
    // 이전 로컬 스토리지 데이터 정리 (HeadacheCheck와 동일하게 처리)
    localStorage.removeItem("cognitiveAnswers");
    
    const newTestSetId = generateTestSetId();
    setCognitiveTestSetId(newTestSetId);
    console.log("새로운 testSetId 생성:", newTestSetId);
    
    navigate(`${prefix}/cognitive/check/new`);
  };

  // 결과 보기 핸들러 (URL 파라미터 방식)
  const handleViewResult = (testSetId: string) => {
    console.log("Viewing test result (Set ID):", testSetId);
    // App.tsx에 정의된 :testSetId 경로로 이동
    navigate(`${prefix}/cognitive/check/results/${testSetId}`);
  };

  // --- 3. 데이터 로딩 (Firebase) ---
  useEffect(() => {
    const getCognitiveCheckHistory = async () => {
      try {
        const userId = getUserId();
        if (!userId) {
          console.error("User ID is missing");
          setIsLoading(false);
          return;
        }

        const q = query(
          collection(db, "userCognitiveResponse"),
          where("userId", "==", userId),
          where("testSetId", "!=", null)
        );

        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          console.log("No cognitive check history found. Redirecting to new test...");
          // 히스토리가 없으면 바로 테스트 시작
          handleStartTest();
        } else {
          const data = querySnapshot.docs.map((doc) => doc.data());
          setResponseHistory(data);
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error fetching cognitive check history:", error);
        setIsLoading(false);
      }
    };
    
    const timer = setTimeout(() => {
      getCognitiveCheckHistory();
    }, 300);

    return () => clearTimeout(timer);
  }, [navigate]);

  // --- 4. 스크롤 이벤트 핸들러 ---
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const handleScroll = () => {
      el.classList.remove(styles.scrollHidden);
      clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = setTimeout(() => el.classList.add(styles.scrollHidden), 400);
    };

    el.addEventListener('scroll', handleScroll);
    el.classList.add(styles.scrollHidden);

    return () => {
      el.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimerRef.current);
    };
  }, []);

  // --- 5. 데이터 그룹화 및 정렬 (렌더링 최적화) ---
  const sortedTestSets = useMemo(() => {
    // testSetId 별로 그룹화
    const grouped = responseHistory.reduce((acc: any, curr: any) => {
      const id = curr.testSetId;
      if (!acc[id]) acc[id] = [];
      acc[id].push(curr);
      return acc;
    }, {});

    // 최신 날짜 순으로 정렬
    return Object.keys(grouped).sort((a, b) => {
       const dateA = grouped[a][0]?.createdAt?.toDate?.() || new Date(0);
       const dateB = grouped[b][0]?.createdAt?.toDate?.() || new Date(0);
       return dateB.getTime() - dateA.getTime();
    }).map(id => ({
      id,
      items: grouped[id]
    }));
  }, [responseHistory]);

  // --- Loading View ---
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={handleBack}>
            <ArrowBack />
          </button>
          <span className={styles.headerText}>Check</span>
        </div>
        <div className={styles.content}>
          <div className={`${styles.contentScroll} ${styles.loadingContainer}`}>
            <div className={styles.spinner}></div>
            <div className={styles.loadingText}>
              {t('cognitive_loading', '인지 능력 검사 기록을 확인하고 있습니다...')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Main View ---
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <ArrowBack />
        </button>
        <span className={styles.headerText}>{t('check_before_1')}</span>
      </div>
      <div className={styles.content}>
        <div className={styles.contentScroll} ref={contentRef}>
          {sortedTestSets.length > 0 ? (
            <>
              {sortedTestSets.map(({ id: testSetId, items: testSet }: any) => {
                const firstItem = testSet[0];
                const currentLanguage = i18n.language;
                const ts = firstItem?.createdAt ?? firstItem?.timestamp;
                const dateObject = ts?.toDate ? ts.toDate() : new Date();
                const createdAt = dateObject.toLocaleDateString(currentLanguage, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                });

                // 점수 초기화 (incomplete 상태)
                const allTestKeys = Object.keys(testMapper);
                const incompleteString = t('incomplete', 'Incomplete');
                const scores = allTestKeys.reduce((acc: any, key: string) => {
                  acc[key] = incompleteString;
                  return acc;
                }, {} as { [key: string]: string | number });

                // 실제 점수 매핑
                testSet.forEach((cur: any) => {
                  const score = cur.results?.totalScore;
                  const testType = cur.testType;
                  if (scores.hasOwnProperty(testType) && typeof score === 'number') {
                    scores[testType] = score;
                  }
                });

                return (
                  <div key={testSetId} className={styles.resultGroup} onClick={() => handleViewResult(testSetId)}>
                    <h3 className={styles.resultDate}>{createdAt}</h3>
                    <div className={styles.resultList}>
                      {Object.entries(scores).map(([type, score]: [string, any]) => {
                        const testConfigKey = testMapper[type as keyof typeof testMapper];
                        const testConfig = (cognitiveData as any)[testConfigKey];
                        const totalCount = testConfig?.totalCount || 0;
                        const threshold = testConfig?.threshold || 0;
                        const scoreClass = getScoreCircleClass(score, threshold, totalCount);

                        return (
                          <div key={type} className={styles.resultItem}>
                            <span className={styles.testName}>{t(`test_type_${type}`)}</span>
                            {typeof score === 'number' ? (
                              <div className={styles.scoreWrapper}>
                                <div className={scoreClass}>
                                  {score}
                                </div>
                                <span className={styles.totalCountText}>/ {totalCount}</span>
                              </div>
                            ) : (
                              <span className={styles.incompleteTag}>
                                {score} 
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <div className={styles.noHistory}>{t("no_history_found")}</div>
          )}
        </div> 
      </div> 
      <button className={styles.startTestButton} onClick={handleStartTest}>
          {t("start_test_button")}
      </button>
    </div>
  );
};

export default CognitiveCheck;